/**
 * Drill Logs List Screen
 * View completed drill logs with filtering and offline sync status using DataTable
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import _ from 'lodash'
import {
  Search,
  Calendar,
  Clock,
  User,
  FileText,
  Download,
  WifiOff,
  Upload,
  Plus,
} from 'lucide-react'
import React, { useState, useRef, useContext } from 'react'
import { useNavigate, useParams } from 'react-router'

import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Button } from '@/components/elements/button'
import { Card, CardContent } from '@/components/elements/card'
import { Input } from '@/components/elements/input'
import { Layout } from '@/components/Layout'
import { usePendingReports, useOnlineStatus, useSyncQueue } from '@/hooks/useOffline'
import { drillTimerApi } from '@/services/drillTimerService'
import { UserStoreContext } from '@/stores/UserStore'

// Helper functions
const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 'N/A'
  const diff = new Date(endTime) - new Date(startTime)
  const minutes = Math.floor(diff / 60000)
  const seconds = Math.floor((diff % 60000) / 1000)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

const createColumns = (handleExportPDF, navigate, entityId) => [
  {
    accessorKey: 'drill_name',
    header: ({ column }) => <SortableColumnHeader column={column} label="Drill Name" />,
    cell: ({ row }) => (
      <button
        type="button"
        onClick={() => navigate(`/accounts/${entityId}/drill-timer/logs/${row.original.id}`)}
        className="text-left font-medium text-blue-600 hover:underline"
      >
        {row.original.drill_name ?? row.original.drillName ?? 'Unknown Drill'}
      </button>
    ),
  },
  {
    accessorKey: 'start_time',
    header: ({ column }) => <SortableColumnHeader column={column} label="Date" />,
    cell: ({ row }) => {
      const startTime = row.original.start_time ?? row.original.startTime
      return (
        <div className="flex items-center gap-1.5 text-sm">
          <Calendar className="text-muted-foreground h-4 w-4" />
          <span>{formatDate(startTime)}</span>
        </div>
      )
    },
  },
  {
    id: 'duration',
    header: () => <span className="text-xs uppercase">Duration</span>,
    cell: ({ row }) => {
      const { duration } = row.original
      const startTime = row.original.start_time ?? row.original.startTime
      const endTime = row.original.end_time ?? row.original.endTime
      return (
        <div className="flex items-center gap-1.5 text-sm">
          <Clock className="text-muted-foreground h-4 w-4" />
          <span className="font-medium">{duration || formatDuration(startTime, endTime)}</span>
        </div>
      )
    },
  },
  {
    id: 'metrics',
    header: () => <span className="text-xs uppercase">Metrics</span>,
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-2 text-xs">
        {row.original.records && (
          <div className="flex items-center gap-1">
            <FileText className="text-muted-foreground h-3 w-3" />
            <span className="font-medium">{row.original.records.length}</span>
            <span className="text-muted-foreground">actions</span>
          </div>
        )}
        {row.original.responders && row.original.responders.length > 0 && (
          <div className="flex items-center gap-1">
            <User className="text-muted-foreground h-3 w-3" />
            <span className="font-medium">{row.original.responders.length}</span>
            <span className="text-muted-foreground">responders</span>
          </div>
        )}
      </div>
    ),
  },
  {
    id: 'proctor',
    header: () => <span className="text-xs uppercase">Proctor</span>,
    cell: ({ row }) => {
      const { proctor } = row.original
      const userName = proctor?.user_name ?? proctor?.userName
      if (!userName) {
        return <span className="text-muted-foreground text-sm">—</span>
      }
      return <span className="text-sm">{userName}</span>
    },
  },
  {
    id: 'actions',
    header: () => <span className="text-xs uppercase">Actions</span>,
    cell: ({ row }) => (
      <Button
        variant="outline"
        size="sm"
        onClick={(e) => handleExportPDF(e, row.original)}
        className="flex items-center gap-2"
      >
        <Download className="h-4 w-4" />
        PDF
      </Button>
    ),
    size: 100,
  },
]

export function DrillLogsListScreen() {
  const navigate = useNavigate()
  const { isEntityOwner, isEntityAdmin, isXverseAdmin } = useContext(UserStoreContext)
  const isOnline = useOnlineStatus()
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sorting, setSorting] = useState([{ id: 'start_time', desc: true }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })
  const pendingCount = usePendingReports()
  const { syncQueue, isSyncing } = useSyncQueue()

  const { entityId } = useParams()

  // Check if user can manage templates
  const canManageTemplates = isXverseAdmin || isEntityOwner || isEntityAdmin

  // Search shrinks the result set, so a deep page can 404 from the paginator.
  // Reset to page 0 in the same handler so React batches the state updates.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  const { data, isLoading } = useQuery({
    queryKey: ['drillLogs', entityId, searchTerm, sorting, pagination],
    queryFn: () => {
      const params = {
        q: searchTerm || undefined,
      }

      // Add ordering parameter if sorting is configured
      if (sorting && sorting.length > 0) {
        const sortField = sorting[0]
        params.ordering = sortField.desc ? `-${sortField.id}` : sortField.id
      }

      // entityId is now part of the URL path, not a query param
      return drillTimerApi.getLogs(entityId, params)
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  })

  const logs = data?.results || data || []

  // Printing happens on the detail view, which owns the print layout —
  // navigate there and let it open the print dialog once the log renders.
  const handleExportPDF = (e, log) => {
    e.stopPropagation()
    navigate(`/accounts/${entityId}/drill-timer/logs/${log.id}`, { state: { autoPrint: true } })
  }

  const columns = createColumns(handleExportPDF, navigate, entityId)

  return (
    <Layout
      breadcrumbs={[
        { pathname: `/accounts/${entityId}/drill-timer`, title: 'Drill Timer' },
        'Logs',
      ]}
    >
      <Layout.Heading title="Drill Logs">
        <div className="flex gap-2">
          {canManageTemplates && (
            <Button variant="outline" onClick={() => navigate('templates')}>
              Manage Templates
            </Button>
          )}
          <Button onClick={() => navigate('setup')}>Start New Drill</Button>
        </div>
      </Layout.Heading>

      {/* Offline Status & Pending Sync - Combined */}
      {(!isOnline || pendingCount > 0) && (
        <Card
          className={`mb-4 ${!isOnline ? 'border-orange-200 bg-orange-50' : 'border-blue-200 bg-blue-50'}`}
        >
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {!isOnline ? (
                  <WifiOff className="h-5 w-5 flex-shrink-0 text-orange-600" />
                ) : (
                  <Upload className="h-5 w-5 flex-shrink-0 text-blue-600" />
                )}
                <div>
                  {!isOnline ? (
                    <>
                      <p className="font-medium text-orange-900">Offline Mode</p>
                      <p className="text-sm text-orange-700">
                        {pendingCount > 0
                          ? `${pendingCount} report${pendingCount !== 1 ? 's' : ''} waiting to sync when connection is restored`
                          : 'You can complete drills offline - they will sync when connection is restored'}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-blue-900">
                        {pendingCount} Report{pendingCount !== 1 ? 's' : ''} Pending Sync
                      </p>
                      <p className="text-sm text-blue-700">
                        {isSyncing ? 'Syncing now...' : 'Ready to sync'}
                      </p>
                    </>
                  )}
                </div>
              </div>
              {isOnline && pendingCount > 0 && (
                <Button
                  onClick={() => syncQueue()}
                  disabled={isSyncing}
                  size="sm"
                  variant="outline"
                  className="border-blue-300 hover:bg-blue-100"
                >
                  {isSyncing ? (
                    <>
                      <Upload className="mr-2 h-4 w-4 animate-pulse" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Sync Now
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Bar */}
      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 md:flex-row">
          <div className="relative w-full md:w-64">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform" />
            <Input
              type="text"
              placeholder="Search by drill name, location..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                debounceSearch(e.target.value)
              }}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* DataTable or Empty State */}
      {(() => {
        if (!isLoading && logs.length === 0) {
          return (
            <Card>
              <CardContent className="py-12 text-center">
                {!isOnline ? (
                  <>
                    <WifiOff className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
                    <h3 className="mb-2 text-lg font-semibold">Offline - No Cached Drill Logs</h3>
                    <p className="text-muted-foreground mb-4 text-sm">
                      You&apos;re currently offline and no drill logs are cached locally.
                      {pendingCount > 0 &&
                        ` You have ${pendingCount} pending report${pendingCount !== 1 ? 's' : ''} that will appear here once synced.`}
                    </p>
                    <Button onClick={() => navigate('setup')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Start New Drill
                    </Button>
                  </>
                ) : (
                  <>
                    <FileText className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
                    <h3 className="mb-2 text-lg font-semibold">No Drill Logs Available</h3>
                    <p className="text-muted-foreground mb-4 text-sm">
                      Start your first drill to see logs here
                    </p>
                    <Button onClick={() => navigate('setup')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Start First Drill
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )
        }

        if (!isLoading && logs.length === 0 && search) {
          return (
            <Card>
              <CardContent className="py-12 text-center">
                <Search className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-semibold">No Results Found</h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  No drill logs match &quot;{search}&quot;. Try adjusting your search.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch('')
                    setSearchTerm('')
                    resetToFirstPage()
                  }}
                >
                  Clear Search
                </Button>
              </CardContent>
            </Card>
          )
        }

        return (
          <DataTable
            columns={columns}
            data={logs}
            loading={isLoading}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
            rowCount={logs.length}
          />
        )
      })()}
    </Layout>
  )
}
